import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  AssessmentOutlined as AssessmentOutlinedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  CloudDoneOutlined as CloudDoneOutlinedIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  DownloadRounded as DownloadRoundedIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon,
  LaunchRounded as LaunchRoundedIcon,
  LockOutlined as LockOutlinedIcon,
  MenuRounded as MenuRoundedIcon,
  SavingsOutlined as SavingsOutlinedIcon,
  SecurityRounded as SecurityRoundedIcon,
  StorageRounded as StorageRoundedIcon,
  TrendingUpRounded as TrendingUpRoundedIcon,
} from '@mui/icons-material';
import { api } from '../api/client';
import './MarketingPage.css';

const REGIONS = [
  ['ap-south-1', 'Asia Pacific (Mumbai)'],
  ['ap-south-2', 'Asia Pacific (Hyderabad)'],
  ['ap-southeast-1', 'Asia Pacific (Singapore)'],
  ['ap-southeast-2', 'Asia Pacific (Sydney)'],
  ['ap-northeast-1', 'Asia Pacific (Tokyo)'],
  ['eu-west-1', 'Europe (Ireland)'],
  ['eu-west-2', 'Europe (London)'],
  ['eu-central-1', 'Europe (Frankfurt)'],
  ['us-east-1', 'US East (N. Virginia)'],
  ['us-east-2', 'US East (Ohio)'],
  ['us-west-1', 'US West (N. California)'],
  ['us-west-2', 'US West (Oregon)'],
];

const EMPTY_FORM = { name: '', company: '', email: '', region: 'ap-south-1' };

function getAuthUrl(path = '/login') {
  const local = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const base = process.env.REACT_APP_AUTH_URL || (local ? 'http://localhost:3000' : 'https://auth.sushaservices.com');
  return `${base.replace(/\/+$/, '')}${path}`;
}

function Brand() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} className="marketing-brand">
      <Box component="img" src="/company-logo.png" alt="Susha Asset Management" />
      <Typography>Susha Asset Management</Typography>
    </Stack>
  );
}

function SectionHeading({ eyebrow, title, body, light = false }) {
  return (
    <Box className="section-heading">
      <Typography className="eyebrow" sx={{ color: light ? '#67d6bd' : '#08745d' }}>{eyebrow}</Typography>
      <Typography component="h2" sx={{ color: light ? '#fff' : '#10233b' }}>{title}</Typography>
      {body && <Typography className="section-copy" sx={{ color: light ? 'rgba(255,255,255,.72)' : '#5d6b7c' }}>{body}</Typography>}
    </Box>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <Box className={`preview-metric ${accent ? 'preview-metric-accent' : ''}`}>
      <Typography>{label}</Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

function ReportPreview() {
  return (
    <Box className="report-preview-wrap" aria-label="Illustrative report preview">
      <Paper className="report-preview">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography className="preview-kicker">Illustrative AWS assessment</Typography>
            <Typography className="preview-title">Cloud overview</Typography>
          </Box>
          <Chip size="small" label="Example" className="example-chip" />
        </Stack>
        <Box className="preview-metrics">
          <Metric label="Monthly direction" value="$4.8k" accent />
          <Metric label="Resources reviewed" value="124" />
          <Metric label="Priority findings" value="7" />
        </Box>
        <Box className="preview-chart" aria-hidden="true">
          {[42, 55, 49, 68, 61, 76, 72, 83, 78, 91, 86, 96].map((height, index) => (
            <span key={index} style={{ height: `${height}%` }} />
          ))}
        </Box>
        <Divider />
        <Stack spacing={1.15} sx={{ mt: 2 }}>
          {[
            ['Unattached storage', 'Review 3 volumes', 'Save'],
            ['Encryption coverage', '2 volumes need attention', 'Secure'],
            ['Cost concentration', 'EC2 is the leading service', 'Review'],
          ].map(([title, detail, tag]) => (
            <Box className="preview-finding" key={title}>
              <CheckCircleRoundedIcon />
              <Box><Typography>{title}</Typography><Typography>{detail}</Typography></Box>
              <span>{tag}</span>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

function StatusPill({ children }) {
  return <span className="status-pill"><CheckCircleRoundedIcon />{children}</span>;
}

function copyText(value) {
  if (navigator.clipboard && value) navigator.clipboard.writeText(value).catch(() => {});
}

function AssessmentReport({ assessment }) {
  const report = assessment?.report || {};
  const score = report.scorecard || {};
  const inventory = report.inventory || {};
  const inventoryItems = [
    ['EC2', inventory.ec2?.total || 0], ['EBS', inventory.ebs?.total || 0],
    ['S3 buckets', inventory.s3?.total || 0], ['RDS', inventory.rds?.total || 0],
    ['Lambda', inventory.lambda?.total || 0], ['DynamoDB', inventory.dynamodb?.total || 0],
  ];
  return (
    <Box className="assessment-result">
      <Box className="result-success"><CheckCircleRoundedIcon /><Box><Typography>Your AWS report is ready</Typography><Typography>Account ending {assessment.accountId?.slice(-4)} · {assessment.region}</Typography></Box></Box>
      <Box className="result-score-grid">
        <Metric label="Projected monthly cost" value={`$${score.projectedMonthlyCost || '0.00'}`} accent />
        <Metric label="Resources reviewed" value={score.totalResources || 0} />
        <Metric label="Cost opportunities" value={score.costOpportunities || 0} />
        <Metric label="Security findings" value={score.securityIssues || 0} />
      </Box>
      <Typography className="result-section-title">Inventory reviewed</Typography>
      <Box className="inventory-grid">
        {inventoryItems.map(([label, value]) => <Box key={label}><Typography>{value}</Typography><Typography>{label}</Typography></Box>)}
      </Box>
      <Typography className="result-section-title">Priority findings</Typography>
      <Stack spacing={1}>
        {(report.findings || []).slice(0, 6).map((finding, index) => (
          <Box className="result-finding" key={`${finding.type}-${index}`}>
            <span>{index + 1}</span>
            <Box><Typography>{finding.msg}</Typography><Typography>{finding.action}</Typography></Box>
          </Box>
        ))}
        {(!report.findings || report.findings.length === 0) && <Alert severity="success">No urgent findings were generated for the selected region.</Alert>}
      </Stack>
      <Alert severity="info" sx={{ mt: 2 }}>A summary has also been sent to {assessment.email}. Delete the SushaCloudReadOnly role in AWS whenever you want to revoke access.</Alert>
    </Box>
  );
}

function AwsAssessmentDialog({ open, onClose }) {
  const [step, setStep] = useState('details');
  const [form, setForm] = useState(EMPTY_FORM);
  const [request, setRequest] = useState(null);
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const createRequest = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/public/cloud/aws/assessments', form, { skipAuthRedirect: true });
      setRequest(data);
      setStep('setup');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'We could not start the assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(request.setup.templateDownloadPath, {
        headers: { 'X-Cloud-Assessment-Token': request.requestToken },
        responseType: 'blob',
        skipAuthRedirect: true,
      });
      const href = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = 'susha-cloud-readonly.json';
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The template could not be downloaded.');
    } finally {
      setLoading(false);
    }
  };

  const validateRole = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        `/public/cloud/aws/assessments/${request.assessment.id}/validate`,
        { roleArn },
        { headers: { 'X-Cloud-Assessment-Token': request.requestToken }, skipAuthRedirect: true },
      );
      setRequest((current) => ({ ...current, assessment: data.assessment }));
      setStep('report');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'AWS validation failed. Check the role ARN and try again.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" PaperProps={{ className: 'assessment-dialog' }}>
      <DialogTitle>
        <Box>
          <Typography className="dialog-kicker">Free AWS cloud assessment</Typography>
          <Typography className="dialog-title">{step === 'details' ? 'Tell us where to send the report' : step === 'setup' ? 'Connect AWS with a read-only role' : 'Your cloud assessment'}</Typography>
        </Box>
        <IconButton onClick={close} disabled={loading} aria-label="Close"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className="dialog-progress">
          {['Details', 'AWS setup', 'Report'].map((label, index) => {
            const active = ['details', 'setup', 'report'].indexOf(step) >= index;
            return <Box className={active ? 'active' : ''} key={label}><span>{index + 1}</span><Typography>{label}</Typography></Box>;
          })}
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'details' && (
          <Box component="form" id="assessment-form" onSubmit={createRequest}>
            <Typography className="dialog-intro">No Susha account is required. Any valid email address works; business-domain verification is not enabled.</Typography>
            <Box className="form-grid">
              <TextField required label="Your name" value={form.name} onChange={update('name')} inputProps={{ maxLength: 100 }} />
              <TextField label="Company (optional)" value={form.company} onChange={update('company')} inputProps={{ maxLength: 160 }} />
              <TextField required type="email" label="Email address" value={form.email} onChange={update('email')} inputProps={{ maxLength: 254 }} />
              <TextField required select label="Primary AWS region" value={form.region} onChange={update('region')}>
                {REGIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Box>
            <Box className="privacy-note"><LockOutlinedIcon /><Typography>We store your contact details, role ARN and report. We never ask for or store AWS access keys.</Typography></Box>
          </Box>
        )}

        {step === 'setup' && request && (
          <Box>
            <Alert severity="info" sx={{ mb: 2.5 }}>Stay signed in to the AWS account you want to assess. The stack creates one revocable IAM role and cannot modify cloud resources.</Alert>
            <Box className="setup-grid">
              <Paper className="setup-step">
                <span className="setup-number">1</span>
                <Typography className="setup-title">Create the AWS role</Typography>
                <Typography className="setup-copy">{request.setup.usesHostedTemplate ? 'Open AWS CloudFormation, review the template and create the stack.' : 'Download the template. In AWS CloudFormation choose Create stack → With new resources → Upload a template file.'}</Typography>
                {request.setup.usesHostedTemplate ? (
                  <Button variant="contained" endIcon={<LaunchRoundedIcon />} onClick={() => window.open(request.setup.launchUrl, '_blank', 'noopener,noreferrer')}>Open AWS setup</Button>
                ) : (
                  <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={downloadTemplate} disabled={loading}>Download template</Button>
                )}
              </Paper>
              <Paper className="setup-step">
                <span className="setup-number">2</span>
                <Typography className="setup-title">Copy the stack output</Typography>
                <Typography className="setup-copy">Wait for CREATE_COMPLETE, open Outputs and copy RoleArn.</Typography>
                <Box className="external-id"><Box><Typography>External ID</Typography><Typography>{request.setup.externalId}</Typography></Box><IconButton onClick={() => copyText(request.setup.externalId)}><ContentCopyRoundedIcon /></IconButton></Box>
              </Paper>
            </Box>
            <TextField fullWidth label="SushaCloudReadOnly role ARN" placeholder="arn:aws:iam::123456789012:role/SushaCloudReadOnly" value={roleArn} onChange={(event) => setRoleArn(event.target.value)} sx={{ mt: 2.5 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2 }} alignItems={{ sm: 'center' }}>
              <Button variant="contained" onClick={validateRole} disabled={loading || !roleArn.trim()} endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardRoundedIcon />}>{loading ? 'Securely reviewing AWS…' : 'Validate and generate report'}</Button>
              <Typography className="validation-time">Usually takes 20–60 seconds.</Typography>
            </Stack>
          </Box>
        )}

        {step === 'report' && <AssessmentReport assessment={request?.assessment} />}
      </DialogContent>
      {step === 'details' && <DialogActions><Button onClick={close}>Cancel</Button><Button form="assessment-form" type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={22} color="inherit" /> : 'Continue to secure AWS setup'}</Button></DialogActions>}
      {step === 'report' && <DialogActions><Button variant="contained" onClick={close}>Done</Button></DialogActions>}
    </Dialog>
  );
}

export default function MarketingPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const year = useMemo(() => new Date().getFullYear(), []);
  const scrollTo = (id) => {
    setMobileNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const steps = [
    ['01', <AssessmentOutlinedIcon />, 'Share basic details', 'Enter your name, any valid email address and the AWS region you want reviewed.'],
    ['02', <LockOutlinedIcon />, 'Approve read-only access', 'Create a narrow cross-account role from our CloudFormation template—no secret keys to paste.'],
    ['03', <CloudDoneOutlinedIcon />, 'Receive the report', 'Susha validates the role, inventories the selected region and shows prioritized findings.'],
  ];
  const faqs = [
    ['Do you need my AWS root login?', 'No. Never share your root login, password, MFA code, access key or secret key. The assessment uses a dedicated read-only IAM role.'],
    ['Can Susha change or delete resources?', 'No. The assessment policy contains read-only inventory and Cost Explorer actions. It does not include create, update or delete actions.'],
    ['How do I remove access?', 'Delete the SushaCloudReadOnly role or its CloudFormation stack in your AWS account. Subsequent role sessions will fail.'],
    ['Do I need a company email?', 'Not currently. Any correctly formatted email address can start the assessment. Company-domain verification can be introduced later.'],
    ['What is included in the first report?', 'Cost direction, EC2 and storage signals, databases, serverless inventory, and a prioritized action list for the selected AWS region.'],
  ];

  return (
    <Box className="marketing-page">
      <Box component="header" className="marketing-header">
        <Container maxWidth="lg" className="header-inner">
          <Brand />
          <Box component="nav" className={`desktop-nav ${mobileNavOpen ? 'mobile-open' : ''}`} aria-label="Primary navigation">
            <Button onClick={() => scrollTo('how-it-works')}>How it works</Button>
            <Button onClick={() => scrollTo('report')}>What you get</Button>
            <Button onClick={() => scrollTo('security')}>Security</Button>
            <Button onClick={() => scrollTo('faq')}>FAQ</Button>
          </Box>
          <Stack direction="row" spacing={1} className="header-actions">
            <Button className="sign-in" onClick={() => window.location.assign(getAuthUrl())}>Sign in</Button>
            <Button variant="contained" onClick={() => setDialogOpen(true)}>Get free report</Button>
            <IconButton className="menu-button" onClick={() => setMobileNavOpen((open) => !open)} aria-label="Toggle navigation"><MenuRoundedIcon /></IconButton>
          </Stack>
        </Container>
      </Box>

      <Box component="main">
        <Box className="hero-section">
          <Container maxWidth="lg" className="hero-grid">
            <Box>
              <Chip icon={<SecurityRoundedIcon />} label="AWS read-only cloud assessment" className="hero-chip" />
              <Typography component="h1">Turn AWS complexity into a clear action plan.</Typography>
              <Typography className="hero-copy">Connect one AWS account without sharing credentials. Susha reviews cost and asset signals, then gives you a concise report your IT and finance teams can act on.</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} className="hero-actions">
                <Button size="large" variant="contained" onClick={() => setDialogOpen(true)} endIcon={<ArrowForwardRoundedIcon />}>Connect AWS and get my report</Button>
                <Button size="large" variant="outlined" onClick={() => scrollTo('report')}>See what is included</Button>
              </Stack>
              <Box className="trust-row">
                <StatusPill>No AWS keys</StatusPill>
                <StatusPill>Read-only role</StatusPill>
                <StatusPill>Revoke anytime</StatusPill>
              </Box>
            </Box>
            <ReportPreview />
          </Container>
        </Box>

        <Box className="provider-bar">
          <Container maxWidth="lg" className="provider-inner">
            <Typography>Cloud intelligence built into Susha ITAM</Typography>
            <Stack direction="row" spacing={{ xs: 2.5, sm: 5 }}>
              <Box className="provider"><Box component="img" src="/aws.png" alt="AWS" /><Box><Typography>AWS</Typography><Typography>Available now</Typography></Box></Box>
              <Box className="provider provider-muted"><Box component="img" src="/azure.jpg" alt="Microsoft Azure" /><Box><Typography>Azure</Typography><Typography>Coming next</Typography></Box></Box>
            </Stack>
          </Container>
        </Box>

        <Box id="how-it-works" className="content-section white-section">
          <Container maxWidth="lg">
            <SectionHeading eyebrow="Simple onboarding" title="From AWS account to useful report in three steps." body="The visitor journey is built for safe self-service. AWS stays the source of truth for authorization." />
            <Box className="three-card-grid">
              {steps.map(([number, icon, title, copy]) => <Paper className="process-card" key={number}><Box className="card-top"><Box className="card-icon">{icon}</Box><Typography>{number}</Typography></Box><Typography className="card-title">{title}</Typography><Typography className="card-copy">{copy}</Typography></Paper>)}
            </Box>
          </Container>
        </Box>

        <Box id="report" className="content-section report-section">
          <Container maxWidth="lg" className="split-grid">
            <Box>
              <SectionHeading eyebrow="Decision-ready output" title="A focused first report—not another raw export." body="The first assessment combines cost direction and resource inventory with practical findings. Detailed operational history can follow inside the Susha workspace." />
              <Button variant="contained" onClick={() => setDialogOpen(true)} endIcon={<ArrowForwardRoundedIcon />}>Start AWS assessment</Button>
            </Box>
            <Box className="outcome-grid">
              {[
                [<TrendingUpRoundedIcon />, 'Cost direction', 'A 30-day cost view and the services driving current spend.'],
                [<StorageRoundedIcon />, 'Asset coverage', 'Compute, storage, database and serverless inventory for the selected region.'],
                [<SavingsOutlinedIcon />, 'Waste signals', 'Stopped compute and unattached storage surfaced for review.'],
                [<SecurityRoundedIcon />, 'Security signals', 'Encryption and monitoring gaps highlighted without changing resources.'],
              ].map(([icon, title, copy]) => <Paper className="outcome-card" key={title}><Box className="card-icon">{icon}</Box><Typography className="card-title">{title}</Typography><Typography className="card-copy">{copy}</Typography></Paper>)}
            </Box>
          </Container>
        </Box>

        <Box id="security" className="content-section security-section">
          <Container maxWidth="lg" className="split-grid security-grid">
            <Box>
              <SectionHeading light eyebrow="Security by design" title="Your credentials never leave AWS." body="Susha requests a temporary session from a dedicated role. The request includes a unique External ID to protect the cross-account trust relationship." />
              <Box className="security-points">
                {['No root access, passwords or MFA codes', 'No AWS access key or secret key', 'Read-only policy with no resource mutations', 'Access ends when you delete the role'].map((item) => <StatusPill key={item}>{item}</StatusPill>)}
              </Box>
            </Box>
            <Paper className="permission-card">
              <Typography className="permission-kicker">WHAT THE ROLE CAN DO</Typography>
              <Typography className="permission-title">Read enough to produce the report. Nothing more.</Typography>
              <Stack spacing={1.2}>
                {['Read Cost Explorer totals', 'Describe regional cloud resources', 'List S3 buckets—never object contents', 'Create no resources and change no settings'].map((item) => <Box className="permission-row" key={item}><CheckCircleRoundedIcon /><Typography>{item}</Typography></Box>)}
              </Stack>
              <Divider sx={{ my: 2.5 }} />
              <Typography className="permission-footnote">Every action is visible in your AWS CloudTrail logs. The role name is SushaCloudReadOnly.</Typography>
            </Paper>
          </Container>
        </Box>

        <Box id="faq" className="content-section white-section">
          <Container maxWidth="md">
            <SectionHeading eyebrow="Clear answers" title="Know exactly what you are connecting." />
            <Stack spacing={1.1}>
              {faqs.map(([question, answer]) => <Accordion key={question} disableGutters elevation={0}><AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography>{question}</Typography></AccordionSummary><AccordionDetails><Typography>{answer}</Typography></AccordionDetails></Accordion>)}
            </Stack>
          </Container>
        </Box>

        <Box className="final-cta">
          <Container maxWidth="lg" className="final-cta-inner">
            <Box><Typography className="eyebrow">START WITH ONE AWS ACCOUNT</Typography><Typography component="h2">See where your cloud needs attention.</Typography><Typography>Use read-only access, get the report, and revoke the role whenever you choose.</Typography></Box>
            <Button size="large" variant="contained" onClick={() => setDialogOpen(true)} endIcon={<ArrowForwardRoundedIcon />}>Generate my free report</Button>
          </Container>
        </Box>
      </Box>

      <Box component="footer" className="marketing-footer">
        <Container maxWidth="lg"><Brand /><Typography>© {year} Susha Services OPC Pvt. Ltd.</Typography></Container>
      </Box>
      <AwsAssessmentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Box>
  );
}
